import {
  ArrowLeft,
  CreditCard,
  Puzzle,
  Settings,
  UserCog,
  Users,
} from "lucide-react";
import { Link, useLocation } from "react-router";

import {
  isWorkspaceAdmin,
  isWorkspaceGroupAdmin,
} from "@/components/helpers/workspace/permissions";
import { SideMenuItem } from "@/components/pages_logged_in/workspace/nav/SideNav/SideMenuItem";
import { SideNavSkeleton } from "@/components/pages_logged_in/workspace/nav/SideNav/SideNavSkeleton";
import { getUser } from "@/components/session/JwtTokenManager";
import { NAV_Z_INDEX } from "@/css/constants";

type SettingsMenu =
  | "users"
  | "groups"
  | "settings"
  | "billing"
  | "integrations";

const Divider = () => <div className="border-b border-border my-3" />;

interface SettingsSideNavContentProps {
  pageLoading: boolean;
}

export function SettingsSideNavContent({
  pageLoading: _pageLoading,
}: SettingsSideNavContentProps) {
  const user = getUser();
  const location = useLocation();

  if (!user) {
    return <SideNavSkeleton />;
  }

  const { pathname } = location;
  let activeMenuItem: SettingsMenu = "users";

  if (pathname.startsWith("/workspace/users")) {
    activeMenuItem = "users";
  } else if (pathname.startsWith("/workspace/groups")) {
    activeMenuItem = "groups";
  } else if (pathname.startsWith("/workspace/settings")) {
    activeMenuItem = "settings";
  } else if (pathname.startsWith("/workspace/billing")) {
    activeMenuItem = "billing";
  } else if (pathname.startsWith("/workspace/integrations")) {
    activeMenuItem = "integrations";
  }

  const menuItems = (
    <>
      {isWorkspaceAdmin() && (
        <SideMenuItem
          icon={Users}
          label="Users"
          active={activeMenuItem === "users"}
          link="/workspace/users"
        />
      )}

      {(isWorkspaceAdmin() || isWorkspaceGroupAdmin()) && (
        <SideMenuItem
          icon={UserCog}
          label="Groups"
          active={activeMenuItem === "groups"}
          link="/workspace/groups"
        />
      )}

      {isWorkspaceAdmin() && (
        <>
          <Divider />
          <SideMenuItem
            icon={Settings}
            label="Workspace"
            active={activeMenuItem === "settings"}
            link="/workspace/settings"
          />

          <SideMenuItem
            icon={CreditCard}
            label="Billing"
            active={activeMenuItem === "billing"}
            link="/workspace/billing"
          />

          <SideMenuItem
            icon={Puzzle}
            label="Integrations"
            active={activeMenuItem === "integrations"}
            link="/workspace/integrations"
          />
        </>
      )}
    </>
  );

  return (
    <div className="h-full flex-col flex justify-between">
      <div>
        <div className="h-[60px] p-4 flex items-center border-b border-border">
          <Link
            to="/home"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
        </div>
        <div className="px-2 py-3">{menuItems}</div>
      </div>
    </div>
  );
}

interface SettingsSideNavProps {
  width: number;
  pageLoading: boolean;
}

export function SettingsSideNav({ width, pageLoading }: SettingsSideNavProps) {
  return (
    <div
      className="fixed top-0 bottom-0 left-0 border-r border-gray-300 bg-white overflow-y-auto"
      style={{
        width: `${width}px`,
        zIndex: NAV_Z_INDEX,
      }}
    >
      <SettingsSideNavContent pageLoading={pageLoading} />
    </div>
  );
}
